<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import YggdrasilTree from './YggdrasilTree.vue'

/**
 * Hero backdrop — the Bifrost crossing to Yggdrasil.
 *
 * Layers, back to front: deep violet night gradient, parallax glow pools,
 * the neon bridge-arc (magenta → gold, with echo lines and a traveling
 * glint), a canvas floor of dotted light waves, and the world-tree.
 *
 * Interactions: mouse parallax on three depths (glows, arc, tree + floor),
 * hover raises the tree's wind amplitude. Everything is rAF-driven with
 * eased targets, paused off-screen, and fully static under
 * prefers-reduced-motion. Listeners live on the parent <header> so the
 * hero's buttons stay usable (this whole layer is pointer-events-none).
 */
const rootRef = ref(null)
const glowRef = ref(null)
const arcRef = ref(null)
const treeRef = ref(null)
const canvasRef = ref(null)
const windy = ref(false)

let ctx = null
let rafId = 0
let running = false
let reducedMotion = false
let resizeObserver = null
let intersectionObserver = null
let parent = null

let width = 0
let height = 0
let dpr = 1
let particles = []

// normalized mouse position, eased toward its target each frame
const mouse = { x: 0, y: 0, tx: 0, ty: 0 }

const ROWS = 9
const GOLD = [255, 180, 105]
const VIOLET = [167, 139, 250]

function buildFloor() {
  particles = []
  const bandTop = height * 0.62
  for (let r = 0; r < ROWS; r++) {
    // closer rows: lower on screen, wider spacing, bigger and brighter dots
    const depth = r / (ROWS - 1)
    const y = bandTop + Math.pow(depth, 1.35) * (height - bandTop - 8)
    const spacing = 20 + depth * 22
    const cols = Math.ceil(width / spacing) + 2
    for (let i = 0; i < cols; i++) {
      const x = i * spacing + (r % 2 ? spacing / 2 : 0)
      // a few golden sparks where the arc lands (right-center)
      const golden = x > width * 0.45 && x < width * 0.85 && (i * 31 + r * 17) % 13 === 0
      particles.push({
        x,
        y,
        depth,
        phase: (i * 13 + r * 29) % 100,
        size: 0.6 + depth * 1.1,
        alpha: 0.07 + depth * 0.3 + (golden ? 0.15 : 0),
        color: golden ? GOLD : VIOLET
      })
    }
  }
}

function resize() {
  const canvas = canvasRef.value
  if (!canvas) return
  const rect = rootRef.value.getBoundingClientRect()
  dpr = Math.min(window.devicePixelRatio || 1, 2)
  width = rect.width
  height = rect.height
  canvas.width = width * dpr
  canvas.height = height * dpr
  canvas.style.width = `${width}px`
  canvas.style.height = `${height}px`
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  buildFloor()
  if (reducedMotion) drawFloor(0)
}

function drawFloor(t) {
  ctx.clearRect(0, 0, width, height)
  for (const p of particles) {
    const wave = reducedMotion ? 0 : Math.sin(t / 2000 + p.x * 0.008 + p.phase) * (1.6 + p.depth * 2.4)
    // deeper rows follow the cursor more — cheap perspective
    const px = p.x + mouse.x * 26 * p.depth
    const py = p.y + wave + mouse.y * 8 * p.depth
    ctx.beginPath()
    ctx.arc(px, py, p.size, 0, Math.PI * 2)
    ctx.fillStyle = `rgba(${p.color[0]},${p.color[1]},${p.color[2]},${p.alpha})`
    ctx.fill()
  }
}

function frame(t) {
  if (!running) return
  mouse.x += (mouse.tx - mouse.x) * 0.06
  mouse.y += (mouse.ty - mouse.y) * 0.06

  // three parallax depths — glows drift with the cursor, arc and tree
  // drift against it, which is what sells the depth
  if (glowRef.value) glowRef.value.style.transform = `translate3d(${mouse.x * 22}px, ${mouse.y * 12}px, 0)`
  if (arcRef.value) arcRef.value.style.transform = `translate3d(${mouse.x * -16}px, ${mouse.y * -9}px, 0)`
  if (treeRef.value) treeRef.value.style.transform = `translate3d(${mouse.x * -9}px, ${mouse.y * -5}px, 0)`

  drawFloor(t)
  rafId = requestAnimationFrame(frame)
}

function start() {
  if (running || reducedMotion) return
  running = true
  rafId = requestAnimationFrame(frame)
}

function stop() {
  running = false
  cancelAnimationFrame(rafId)
}

function onPointerMove(e) {
  const rect = parent.getBoundingClientRect()
  mouse.tx = ((e.clientX - rect.left) / rect.width) * 2 - 1
  mouse.ty = ((e.clientY - rect.top) / rect.height) * 2 - 1
}

function onPointerEnter(e) {
  if (e.pointerType === 'mouse') windy.value = true
}

function onPointerLeave() {
  windy.value = false
  mouse.tx = 0
  mouse.ty = 0
}

onMounted(() => {
  ctx = canvasRef.value.getContext('2d')
  reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  parent = rootRef.value.parentElement

  resize()
  resizeObserver = new ResizeObserver(resize)
  resizeObserver.observe(rootRef.value)

  parent.addEventListener('pointermove', onPointerMove, { passive: true })
  parent.addEventListener('pointerenter', onPointerEnter, { passive: true })
  parent.addEventListener('pointerleave', onPointerLeave, { passive: true })

  intersectionObserver = new IntersectionObserver(([entry]) => {
    entry.isIntersecting ? start() : stop()
  })
  intersectionObserver.observe(rootRef.value)

  if (reducedMotion) drawFloor(0)
  else start()
})

onUnmounted(() => {
  stop()
  resizeObserver?.disconnect()
  intersectionObserver?.disconnect()
  parent?.removeEventListener('pointermove', onPointerMove)
  parent?.removeEventListener('pointerenter', onPointerEnter)
  parent?.removeEventListener('pointerleave', onPointerLeave)
})
</script>

<template>
  <div ref="rootRef" class="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
    <!-- Deep violet night -->
    <div
      class="absolute inset-0"
      style="
        background:
          radial-gradient(120% 90% at 72% 38%, #4c2ba8 0%, #2a1763 34%, transparent 70%),
          radial-gradient(90% 70% at 18% 12%, #1b1150 0%, transparent 60%),
          radial-gradient(140% 80% at 55% 108%, #3b2087 0%, transparent 55%),
          linear-gradient(180deg, #0a0620 0%, #150c3d 55%, #0b0722 100%);
      "
    ></div>

    <!-- Parallax glow pools (magenta at the arc's apex, gold at its landing) -->
    <div ref="glowRef" class="absolute inset-0 will-change-transform">
      <div class="absolute left-[30%] top-[30%] w-[46rem] h-[20rem] rounded-full blur-[100px] bg-[#c445d8]/20"></div>
      <div class="absolute right-[4%] top-[44%] w-[30rem] h-[15rem] rounded-full blur-[90px] bg-[#ff9e4d]/15"></div>
    </div>

    <!-- The Bifrost — neon bridge-arc with echo lines and a traveling glint -->
    <svg
      ref="arcRef"
      class="absolute inset-0 w-full h-full will-change-transform"
      viewBox="0 0 1440 800"
      preserveAspectRatio="xMidYMax slice"
      fill="none"
    >
      <defs>
        <linearGradient id="bifrost-arc" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stop-color="#7c3aed" stop-opacity="0" />
          <stop offset="0.28" stop-color="#c94fe0" />
          <stop offset="0.55" stop-color="#ff9e4d" />
          <stop offset="0.78" stop-color="#ffd9a2" />
          <stop offset="1" stop-color="#ff9e4d" stop-opacity="0" />
        </linearGradient>
        <filter id="bifrost-blur" x="-20%" y="-60%" width="140%" height="220%">
          <feGaussianBlur stdDeviation="9" />
        </filter>
      </defs>

      <!-- Echo lines fanning out to the left, like wind trails -->
      <g stroke="#b39bff" stroke-linecap="round">
        <path d="M-140 450 C 300 140, 700 140, 1440 420" stroke-width="1" opacity="0.1" />
        <path d="M-140 520 C 320 210, 740 190, 1500 470" stroke-width="1.2" opacity="0.16" />
        <path d="M-160 580 C 340 260, 760 220, 1530 505" stroke-width="1.4" opacity="0.22" />
        <path d="M-160 660 C 340 350, 800 300, 1560 570" stroke-width="1.2" opacity="0.13" />
      </g>

      <!-- Main arc: halo, body, white-hot core -->
      <path
        d="M-120 640 C 330 300, 780 250, 1560 540"
        stroke="url(#bifrost-arc)"
        stroke-width="16"
        opacity="0.32"
        filter="url(#bifrost-blur)"
        stroke-linecap="round"
      />
      <path
        d="M-120 640 C 330 300, 780 250, 1560 540"
        stroke="url(#bifrost-arc)"
        stroke-width="3.5"
        opacity="0.9"
        stroke-linecap="round"
      />
      <path
        d="M-120 640 C 330 300, 780 250, 1560 540"
        stroke="#fff3da"
        stroke-width="1.4"
        opacity="0.95"
        stroke-linecap="round"
      />
      <!-- Traveling glint (pathLength normalizes the dash math) -->
      <path
        class="arc-glint"
        d="M-120 640 C 330 300, 780 250, 1560 540"
        pathLength="2400"
        stroke="#ffffff"
        stroke-width="2.4"
        stroke-linecap="round"
        stroke-dasharray="90 2310"
        opacity="0.75"
      />
    </svg>

    <!-- Floor of dotted light waves -->
    <canvas ref="canvasRef" class="absolute inset-0"></canvas>

    <!-- Yggdrasil — recedes behind the copy on small screens -->
    <div
      ref="treeRef"
      class="absolute bottom-0 right-[-4rem] sm:right-[-2rem] lg:right-0 h-[62%] sm:h-[74%] lg:h-[90%] aspect-[640/800] will-change-transform opacity-25 sm:opacity-40 lg:opacity-95 transition-opacity duration-500"
    >
      <div class="absolute inset-x-0 top-[8%] h-[55%] rounded-full blur-[80px] bg-[#6d3bd9]/25"></div>
      <YggdrasilTree :windy="windy" class="relative" />
    </div>

    <!-- Vignette keeps the copy column readable -->
    <div
      class="absolute inset-0"
      style="background: radial-gradient(120% 100% at 50% 40%, transparent 55%, rgba(5, 3, 15, 0.55) 100%)"
    ></div>
  </div>
</template>

<style scoped>
.arc-glint {
  animation: bifrost-glint 14s linear infinite;
}

@keyframes bifrost-glint {
  from { stroke-dashoffset: 2400; }
  to { stroke-dashoffset: 0; }
}

@media (prefers-reduced-motion: reduce) {
  .arc-glint {
    animation: none;
    opacity: 0;
  }
}
</style>
